
import PropTypes from 'prop-types';
import React, {
  useRef, useState, useEffect, useLayoutEffect, useCallback,
} from 'react';
import { createPortal } from 'react-dom';
import Base from './Base';
import config from './config';

/**
 * Styled tooltip that shows on hover.
 *
 * The box is portaled to document.body so it can't be clipped by — or add
 * scrollWidth/scrollHeight to — an ancestor with overflow (scroll containers
 * were clipping top-row tooltips and growing stray scrollbars). Position is
 * computed from the trigger's viewport rect and clamped to the viewport; the
 * arrow re-anchors to the trigger center when the box is clamped at an edge.
 */

const EDGE_MARGIN = 8; // keep the box this far from the viewport edge
const GAP = 8; // vertical gap between trigger and box

const Tooltip = ({
  title,
  inverted = true,
  rounded = true,
  triggerStyle = null,
  children,
  ...props
}) => {
  const { fontFamily, fontSizes, scale, colors } = config;

  const triggerRef = useRef(null);
  const boxRef = useRef(null);
  // viewport coords of the trigger while hovered; null when hidden
  const [anchor, setAnchor] = useState(null);
  // resolved box placement after measuring; null until measured
  const [pos, setPos] = useState(null);

  const measure = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setAnchor({ cx: r.left + r.width / 2, top: r.top, bottom: r.bottom });
  }, []);

  const hide = () => {
    setAnchor(null);
    setPos(null);
  };

  // while open, the fixed box would drift from the trigger on scroll/resize; re-measure to track it.
  // scroll capture phase catches scrolling inside overflow ancestors (e.g. the autoscale table), not just the page.
  const open = anchor !== null;
  useEffect(() => {
    if (!open) return undefined;
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [open, measure]);

  useLayoutEffect(() => {
    if (!anchor || !boxRef.current) return;
    const box = boxRef.current;
    const left = Math.max(
      EDGE_MARGIN,
      Math.min(anchor.cx - box.offsetWidth / 2, window.innerWidth - box.offsetWidth - EDGE_MARGIN),
    );
    // prefer above the trigger; flip below when there isn't room (e.g. top-row triggers)
    const above = anchor.top - box.offsetHeight - GAP >= EDGE_MARGIN;
    const top = above ? anchor.top - box.offsetHeight - GAP : anchor.bottom + GAP;
    setPos({
      left, top, arrowLeft: anchor.cx - left, above,
    });
  }, [anchor]);

  const sx = {
    root: {
      position: 'relative',
      display: 'inline-block',
      cursor: 'pointer',
    },
    box: {
      position: 'fixed',
      top: 0,
      left: 0,
      // hide until measured/positioned to avoid a one-frame flash at 0,0
      visibility: pos ? 'visible' : 'hidden',
      transform: pos ? `translate(${pos.left}px, ${pos.top}px)` : 'none',
      // portaled to document.body, so the app root's inherited font is lost
      fontFamily,
      fontSize: fontSizes[6],
      whiteSpace: 'nowrap',
      paddingTop: scale[1] / 2,
      paddingBottom: scale[1] / 2,
      paddingLeft: scale[1],
      paddingRight: scale[1],
      color: colors.white,
      backgroundColor: colors.black,
      // top-most transient layer: the portaled box must paint above panel
      // overlays (20), SimExpiration (100), and the fullscreen editor layers
      // (9999/10000); pointer-events none keeps it inert regardless
      zIndex: 10001,
      pointerEvents: 'none',
    },
    arrow: {
      position: 'absolute',
      left: pos ? `${pos.arrowLeft}px` : '50%',
      border: '6px solid transparent',
      transform: 'translate(-50%, 0)',
      // arrow points toward the trigger: down when box is above, up when flipped below
      ...(pos && !pos.above
        ? { bottom: '100%', borderBottomColor: colors.black }
        : { top: '100%', borderTopColor: colors.black }),
    },
  };

  // A span, so a tooltip can sit inside a Text paragraph; the box itself is
  // portaled to body. The trigger is inline-block either way (sx.root).
  return (
    <span
      ref={triggerRef}
      className="Tooltip"
      aria-label={title}
      // `style`/props flow to the portaled box; triggerStyle is the only way
      // to style this trigger element (e.g. inline-flex centering)
      style={{ ...sx.root, ...triggerStyle }}
      onMouseEnter={measure}
      onMouseLeave={hide}
      onFocus={measure}
      onBlur={hide}
    >
      {children}
      {anchor
        && createPortal(
          <Base
            {...props}
            inverted={inverted}
            rounded={rounded}
            baseStyle={sx.box}
            className="Tooltip_box"
            baseRef={(node) => {
              boxRef.current = node;
            }}
          >
            {title}
            <div className="Tooltip_arrow" style={sx.arrow} />
          </Base>,
          document.body,
        )}
    </span>
  );
};

Tooltip.propTypes = {
  /** Text to display in tooltip */
  title: PropTypes.node,
  /** Extra style for the trigger element (style/props target the popup box) */
  triggerStyle: PropTypes.object,
};

export default Tooltip;
